/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import { Select, Switch, Tooltip } from 'antd'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { useRelationshipTypes } from '@sportninja/common/hooks/useRelationshipTypes'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import ProfilePlayerStatsRow from '../../components/ProfilePlayerStatsRow'
import QuestionIcon from 'src/components/icons/QuestionIcon'
import {
  FAMILY_SUPPORT_URL,
  SELF_RELATIONSHIP_FORM_LABEL,
  SELF_RELATIONSHIP_TYPE_ID,
} from '@sportninja/common/features/profile/family/constants'
import { TextInput } from 'src/components/ts-components/TextInput'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import {
  font,
  weightBold,
  weightMedium,
  paragraphSmall,
  paragraphXSmall,
  paragraphLarge,
} from 'src/components/css'

export interface FamilyContactFormValues {
  nameFirst: string
  nameLast: string
  email: string
  phone: string
  relationship: number | null
  /** Whether the contact gets administrative access to the player. */
  isPrimary: boolean
}

export interface FamilyContactFormPlayer {
  id: string
  name_full?: string | null
  name?: string | null
  image?: { full_path?: string | null } | null
  /** Stat fields for the chips under the name (age / weight / height). */
  birth_date?: string | null
  height?: string | number | null
  weight?: string | number | null
}

interface InvitePlayerCardProps {
  player: FamilyContactFormPlayer
}

const InvitePlayerCard: React.FC<InvitePlayerCardProps> = ({ player }) => {
  const avatarSrc =
    getImageThumbnailId({ image: player.image }) ||
    '/images/generic_placeholders/placeholder_player.png'
  const displayName = player.name_full || player.name || ''

  return (
    <div css={playerCardWrapperStyle}>
      {/* Caption sits ABOVE the card, centered — per Figma 19506:11865. */}
      <span css={playerCardCaptionStyle}>
        They&apos;ll have access to this player:
      </span>
      <div css={playerCardStyle}>
        <img
          src={avatarSrc}
          alt={displayName}
          css={playerAvatarStyle}
          onError={(e) => {
            const img = e.currentTarget as HTMLImageElement
            img.src = '/images/generic_placeholders/placeholder_player.png'
          }}
        />
        <div css={playerInfoColStyle}>
          <span css={playerNameStyle}>{displayName}</span>
          <ProfilePlayerStatsRow player={player} compact />
        </div>
      </div>
    </div>
  )
}

interface Props {
  values: FamilyContactFormValues
  onChange: (next: FamilyContactFormValues) => void
  player: FamilyContactFormPlayer | null
  /**
   * Locks the identity fields (first/last name, email, phone) — used when
   * editing a contact who has already ACCEPTED the invite: only their
   * relationship and admin-access can still be changed. Defaults to false.
   */
  lockBaseFields?: boolean
  /**
   * Inline per-field API errors keyed by form field name (nameFirst, nameLast,
   * email). Threaded into the matching TextInputs; the relationship/phone/admin
   * controls have no inline error slot, so their errors surface via the toast.
   */
  fieldErrors?: Record<string, string>
  /**
   * Include the Self ("Me") relationship as a selectable option, relabeled to
   * SELF_RELATIONSHIP_FORM_LABEL. Only the EDIT flow sets this — it lets an
   * existing contact be marked as the player themselves. Defaults false so the
   * invite flow keeps Self hidden.
   */
  allowSelfRelationship?: boolean
}

const FamilyContactForm: React.FC<Props> = ({
  values,
  onChange,
  player,
  lockBaseFields = false,
  fieldErrors = {},
  allowSelfRelationship = false,
}) => {
  const { relationshipTypes, loading: relationshipsLoading } =
    useRelationshipTypes({ includeSelf: allowSelfRelationship })

  // In the edit flow the Self entry is included but relabeled so the choice
  // reads as an action ("Mark this member as me") rather than the bare "Me".
  const relationshipOptions = relationshipTypes.map((rt) => ({
    value: rt.id,
    label:
      rt.id === SELF_RELATIONSHIP_TYPE_ID
        ? capitalizeWords(SELF_RELATIONSHIP_FORM_LABEL)
        : rt.name,
  }))

  return (
    <div css={formContainerStyle}>
      {/* Keep wheel/touch scrolling inside the relationship dropdown from
          chaining to the modal/page behind it — that chaining scrolls the
          background and makes the anchored popup jump as you scroll options. */}
      <Global
        styles={css`
          .family-relationship-dropdown .rc-virtual-list-holder {
            overscroll-behavior: contain;
          }
        `}
      />
      {player && <InvitePlayerCard player={player} />}

      <div css={twoColRowStyle}>
        <div css={fieldGroupStyle}>
          <TextInput
            name='nameFirst'
            value={values.nameFirst}
            setValue={(v) => onChange({ ...values, nameFirst: v })}
            label={capitalizeWords('First Name')}
            placeholder='e.g. John'
            fieldErrors={fieldErrors}
            customError={null}
            disabled={lockBaseFields}
            required
          />
        </div>
        <div css={fieldGroupStyle}>
          <TextInput
            name='nameLast'
            value={values.nameLast}
            setValue={(v) => onChange({ ...values, nameLast: v })}
            label={capitalizeWords('Last Name')}
            placeholder='e.g. Smith'
            fieldErrors={fieldErrors}
            customError={null}
            disabled={lockBaseFields}
            required
          />
        </div>
      </div>

      <div css={twoColRowStyle}>
        <div css={fieldGroupStyle}>
          <TextInput
            name='email'
            value={values.email}
            setValue={(v) => onChange({ ...values, email: v })}
            label={capitalizeWords('Email')}
            placeholder='name@example.com'
            fieldErrors={fieldErrors}
            customError={null}
            disabled={lockBaseFields}
            required
          />
        </div>
        <div css={fieldGroupStyle}>
          <span css={selectLabelStyle}>
            {capitalizeWords('Phone Number')}
            <span css={optionalSuffixStyle}> (Optional)</span>
          </span>
          {/* ModernPhoneInput renders its own (empty) label + an 8px internal
              gap; collapse both so the phone input's top aligns with the Email
              input alongside it. */}
          <div css={phoneInputWrapperStyle}>
            <ModernPhoneInput
              value={values.phone}
              onChange={(v) => onChange({ ...values, phone: v })}
              label=''
              disabled={lockBaseFields}
            />
          </div>
        </div>
      </div>

      <div css={twoColRowStyle}>
        <div css={fieldGroupStyle}>
          <span css={selectLabelStyle}>
            {capitalizeWords('Relationship')}
            {/* Cosmetic required marker — mirrors TextInput's `required` asterisk. */}
            <span css={requiredMarkStyle}>*</span>
          </span>
          <Select
            value={values.relationship}
            onChange={(v: number | null) =>
              onChange({ ...values, relationship: v ?? null })
            }
            placeholder={
              relationshipsLoading
                ? capitalizeWords('Loading…')
                : capitalizeWords('Select…')
            }
            loading={relationshipsLoading}
            css={selectStyle}
            options={relationshipOptions}
            popupClassName='family-relationship-dropdown'
            // Taller than the 256px default so the full list (incl. the trailing
            // "Mark this member as me" option) shows with less scrolling.
            listHeight={320}
            // The modal wrapper sits at z-index 10001; the Select popup renders
            // in a portal, so without a higher z-index it appears UNDER the
            // modal.
            dropdownStyle={{ zIndex: 10050 }}
          />
        </div>
        <div css={fieldGroupStyle}>
          <div css={labelRowStyle}>
            <span css={selectLabelStyle}>
              {capitalizeWords('Has Administrative Access')}
            </span>
            <Tooltip
              title={capitalizeWords('Learn more about administrative access.')}
              zIndex={10050}
            >
              <a
                css={labelInfoButtonStyle}
                href={FAMILY_SUPPORT_URL}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Learn more about administrative access'
              >
                <QuestionIcon size={18} color={colors.PRIMARY} />
              </a>
            </Tooltip>
          </div>
          <div css={toggleControlStyle}>
            <Switch
              checked={values.isPrimary}
              onChange={(checked) =>
                onChange({ ...values, isPrimary: checked })
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default FamilyContactForm

// ─── Styles ────────────────────────────────────────────────────────────────

const formContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const playerCardWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const playerCardCaptionStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_50};
  text-align: center;
`

const playerCardStyle = css`
  background-color: ${colors.SECONDARY_200};
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`

const playerAvatarStyle = css`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

// Name on line 1, stat chips on line 2 — matches the player-switch row layout.
const playerInfoColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const playerNameStyle = css`
  ${paragraphLarge}
  ${weightBold}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

const fieldGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

// Two fields side by side; each takes half the row. Used for First/Last,
// Email/Phone, and Relationship/Administrative Access.
const twoColRowStyle = css`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
    min-width: 0;
  }
`

const selectLabelStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_0};
  ${font.body}
`

// Label + inline help icon on one line (used for Administrative Access).
const labelRowStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const labelInfoButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }
`

// Holds the toggle under its label, height-matched to the adjacent 40px Select.
const toggleControlStyle = css`
  display: flex;
  align-items: center;
  min-height: 40px;
`

// ModernPhoneInput ships its own label element + an 8px internal gap. We render
// the label ourselves (above, with the (Optional) suffix), so collapse the
// component's empty label and top gap — otherwise the phone input sits ~28px
// lower than the Email input it shares a row with.
const phoneInputWrapperStyle = css`
  & > div {
    gap: 0;
  }
  & label {
    display: none;
  }
`

const optionalSuffixStyle = css`
  ${paragraphXSmall}
  ${font.body}
  color: ${colors.NEUTRAL_50};
`

// Matches the asterisk TextInput renders for its `required` prop, so the Select's
// label lines up with the text fields alongside it.
const requiredMarkStyle = css`
  margin-left: 4px;
`

const selectStyle = css`
  width: 100%;

  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid ${colors.SECONDARY_100} !important;
    background: ${colors.SECONDARY_200} !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    color: ${colors.NEUTRAL_50};
    font-size: 14px;
    line-height: 40px !important;
  }

  .ant-select-selection-item {
    color: ${colors.NEUTRAL_0};
  }

  .ant-select-arrow {
    color: ${colors.NEUTRAL_50};
  }

  .ant-select-clear {
    background: ${colors.SECONDARY_200};
    color: ${colors.NEUTRAL_50};
  }
`
